<template>
  <div
    class="media-gallery-carousel"
    tabindex="0"
    aria-label="Produktbilder"
    role="region"
    @keydown.left.prevent="prev"
    @keydown.right.prevent="next"
  >
    <ssr-carousel
      ref="carousel"
      gutter="0"
      :show-arrows="true"
      @change="pageChange"
    >
      <div
        class="slide"
        v-for="(images, index) in gallery"
        :key="images.src"
      >
        <div
          class="product-image-container"
          :class="{'video-container w-100 h-100 flex relative': images.video}"
        >
          <product-image
            v-if="index > 0"
            v-show="hideImageAtIndex !== index"
            @click="openOverlay"
            class="image pointer"
            :class="{'product-image--video': images.video}"
            :image="images"
            :alt="galleryImageAlt(index)"
          />
          <product-image
            v-if="index === 0"
            v-show="hideImageAtIndex !== index"
            @click="openOverlay"
            class="image pointer"
            :class="{'product-image--video': images.video}"
            :image="images"
            :alt="galleryImageAlt(index)"
            unlazy
            high-priority
          />
          <product-video
            v-if="images.video && (index === currentPage)"
            v-bind="images.video"
            :index="index"
            @video-started="onVideoStarted"
          />
        </div>
      </div>
    </ssr-carousel>

    <div class="product-gallery-thumbs-container">
      <div @click="scrollThumbs('left')" ref="thumbsLeft" :class="{ active: thumbsLeft }" class="product-gallery-thumbs-arrows product-gallery-thumbs-arrows-left" />
      <ul @scroll="scrollThumbsScrolled" ref="thumbs" class="product-gallery-thumbs">
        <li class="product-gallery-thumb" v-for="(images, index) in gallery" :key="images.src">
          <product-image
            :class="{'thumb-video': images.video}"
            @click="navigate(index)"
            :image="{ src: images.loading }"
            :alt="galleryImageAlt(index)"
          />
        </li>
      </ul>
      <div @click="scrollThumbs('right')" ref="thumbsRight" :class="{ active: thumbsRight }" class="product-gallery-thumbs-arrows product-gallery-thumbs-arrows-right" />
    </div>
    <i
      class="zoom-in"
      @click="openOverlay"
    ><!--zoom_in--></i>
  </div>
</template>

<script>
import config from 'config'
import { htmlDecode } from '@vue-storefront/core/lib/store/filters'
import ProductImage from './ProductImage'
import ProductVideo from './ProductVideo'
import reduce from 'lodash-es/reduce'
import map from 'lodash-es/map'

import SsrCarousel from 'vue-ssr-carousel'
import ssrCarouselCss from 'vue-ssr-carousel/index.css'

export default {
  name: 'ProductGalleryCarousel',
  components: {
    ProductImage,
    ProductVideo,
    'ssr-carousel': SsrCarousel
  },
  props: {
    gallery: {
      type: Array,
      required: true
    },
    productName: {
      type: String,
      required: true,
      default: ''
    },
    configuration: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      carouselTransition: true,
      carouselTransitionSpeed: 0,
      currentColor: 0,
      currentPage: 0,
      hideImageAtIndex: null,
      thumbsLeft: false,
      thumbsRight: true
    }
  },
  computed: {},
  beforeMount () {
    this.$bus.$on('filter-changed-product', this.selectVariant)
    this.$bus.$on('product-after-load', this.selectVariant)
  },
  mounted () {
    this.selectVariant()

    if (this.configuration.color) {
      const { color } = this.configuration
      this.currentColor = color.id
    }

    this.$emit('loaded')
  },
  beforeDestroy () {
    this.$bus.$off('filter-changed-product', this.selectVariant)
    this.$bus.$off('product-after-load', this.selectVariant)
  },
  methods: {
    galleryImageAlt (index) {
      const name = htmlDecode(this.productName || '')
      const n = typeof index === 'number' ? index + 1 : 1
      return name ? `${name} – Bild ${n}` : `Produktbild ${n}`
    },
    navigate (index) {
      if (this.$refs.carousel) {
        this.$refs.carousel.goto(index)
      }
    },
    next () {
      if (this.$refs.carousel) this.$refs.carousel.next()
    },
    prev () {
      if (this.$refs.carousel) this.$refs.carousel.back()
    },
    selectVariant () {
      if (config.products.gallery.mergeConfigurableChildren) {
        const option = reduce(map(this.configuration, 'attribute_code'), (result, attribute) => {
          result[attribute] = this.configuration[attribute].id
          return result
        }, {})
        if (option) {
          let index = this.gallery.findIndex(
            obj => obj.id && Object.entries(obj.id).toString() === Object.entries(option).toString(), option)
          if (index < 0) index = this.gallery.findIndex(obj => obj.id && obj.id.color === option.color)
          this.navigate(index)
        }
      }

      this.$emit('close');
    },
    openOverlay () {
      const currentSlide = this.$refs.carousel.currentPage
      this.$emit('toggle', currentSlide)
    },
    switchCarouselSpeed () {
      const { color } = this.configuration
      if (color && this.currentColor !== color.id) {
        this.currentColor = color.id
        this.carouselTransitionSpeed = 0
      } else {
        this.carouselTransitionSpeed = 500
      }
    },
    pageChange (data) {
      this.switchCarouselSpeed()

      this.currentPage = data.index
      this.hideImageAtIndex = null
    },
    onVideoStarted (index) {
      this.hideImageAtIndex = index
    },
    scrollThumbs (direction) {
      let pixels = 0;
      const thumbs = this.$refs.thumbs;

      if (direction === 'left') {
        pixels = -225;
      } else {
        pixels = 225;
      }

      thumbs.scrollBy({
        top: 0,
        left: pixels,
        behavior: 'smooth'
      });
    },
    scrollThumbsScrolled () {
      const thumbs = this.$refs.thumbs;
      const thumbsLeft = this.$refs.thumbsLeft;
      const thumbsRight = this.$refs.thumbsRight;

      if (thumbs.scrollLeft === 0) {
        this.thumbsLeft = false;
      } else {
        this.thumbsLeft = true;
      }
      if (thumbs.scrollLeft >= (thumbs.scrollWidth - thumbs.clientWidth)) {
        this.thumbsRight = false;
      } else {
        this.thumbsRight = true;
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~theme/css/animations/transitions';
.media-gallery-carousel {
  position: relative;
  text-align: center;
  height: 100%;
  outline: none;

  &:focus-visible {
    outline: 2px solid currentcolor;
    outline-offset: 4px;
  }
}
.image{
  opacity: 1;
  will-change: opacity;
  transition: .3s opacity $motion-main;
  &:hover{
    opacity: .9;
  }
}
.video-container {
  align-items: center;
  justify-content: center;
}
</style>
